import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    Grid,
    TextField,
    Typography,
} from "@mui/material";
import { RadioCard } from "../../../core/components/RadioCard";
import { ExpandMore, WorkspacePremium } from "@mui/icons-material";
import { yupResolver } from "@hookform/resolvers/yup";
import { FieldError, FieldValues, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useEstabelecimentos } from "../../hooks/useEstabelecimentos";
import { useSnackbar } from "../../../core/contexts/SnackbarContext";
import { useAutenticacaoContext } from "../../../core/contexts/AutenticacaoContext";
import { useMascara } from "../../../core/hooks/useMascara";
import { criarEstabelecimentoSchema } from "../../schemas/criarEstabelecimentoSchema";
import { ActionsContainer, BotaoFreeTrial, FormWrapper, PageContainer, PlanoBannerInner, PlanoBannerOuter, PlanoBannerWrapper, PopularBadge } from "./styles";

const PLANO_FREE_TRIAL_UUID = "70570cbc-cd47-45e0-b3f0-6adaf7891008";
const PLANO_FREE_TRIAL_NOME = "Cozinheiro";

interface ICriarEstabelecimentoForm {
    tipoPessoa: string;
    cnpj?: string;
    cpf?: string;
    nomeFantasia: string;
    razaoSocial?: string;
    endereco: {
        cep: string;
        rua: string;
        numero: string;
        complemento?: string;
        bairro: string;
        cidade: string;
        uf: string;
    };
}

export const CriarEstabelecimento = () => {
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const { usuario, alterarAcesso } = useAutenticacaoContext();
    const { criarEstabelecimento, buscarEstabelecimentosUsuario, loading } = useEstabelecimentos();
    const { mascaraCNPJ, mascaraCEP, mascaraCPF, formatarTelefoneParaPadrao } = useMascara();

    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<ICriarEstabelecimentoForm>({
        resolver: yupResolver(criarEstabelecimentoSchema) as any,
        defaultValues: { tipoPessoa: "" },
        mode: "onChange",
    });

    const tipoPessoa = watch("tipoPessoa");
    const isPJ = tipoPessoa === "PJ";
    const formVisivel = tipoPessoa === "PJ" || tipoPessoa === "PF";

    const enderecoErrors = errors.endereco as Record<string, FieldError | undefined> | undefined;

    const handleTipoPessoaChange = (novo: "PF" | "PJ") => {
        setValue("tipoPessoa", novo, { shouldValidate: false });
        setValue("cnpj", "");
        setValue("cpf", "");
        setValue("razaoSocial", "");
    };

    const handleBuscarCep = async (cep: string) => {
        const cepLimpo = cep.replace(/\D/g, "");
        if (cepLimpo.length !== 8) return;

        try {
            const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
            const data = await response.json();
            if (!data.erro) {
                setValue("endereco.rua", data.logradouro || "");
                setValue("endereco.bairro", data.bairro || "");
                setValue("endereco.cidade", data.localidade || "");
                setValue("endereco.uf", data.uf || "");
            }
        } catch {
            // Silencioso - usuário pode preencher manualmente
        }
    };

    const handleSubmitForm = async (dados: FieldValues) => {
        const cepLimpo = dados.endereco.cep.replace(/\D/g, "");
        const telefoneLimpo = formatarTelefoneParaPadrao(usuario?.telefone) ?? "";

        const resultado = await criarEstabelecimento({
            tipoPessoa: dados.tipoPessoa,
            ...(dados.tipoPessoa === "PJ"
                ? { cnpj: dados.cnpj.toUpperCase().replace(/[^A-Z0-9]/g, ""), razaoSocial: dados.razaoSocial, nomeFantasia: dados.nomeFantasia }
                : { cpf: dados.cpf.replace(/\D/g, ""), nomeCompleto: usuario?.nome, nomeFantasia: dados.nomeFantasia }),
            email: usuario?.email ?? "",
            telefone: telefoneLimpo,
            usuarioCriadorUuid: usuario?.uuid ?? "",
            endereco: {
                cep: cepLimpo,
                rua: dados.endereco.rua,
                numero: dados.endereco.numero,
                complemento: dados.endereco.complemento || undefined,
                bairro: dados.endereco.bairro,
                cidade: dados.endereco.cidade,
                uf: dados.endereco.uf,
            },
            planoUuid: PLANO_FREE_TRIAL_UUID,
        });

        if (resultado) {
            const novoEstabelecimentoUuid = resultado.estabelecimento?.uuid;
            const acessos = await buscarEstabelecimentosUsuario();
            const novoAcesso = acessos?.find((a: any) => a.estabelecimentoUuid === novoEstabelecimentoUuid);

            if (novoAcesso?.uuid) {
                await alterarAcesso(novoAcesso.uuid);
            } else {
                snackbar("Estabelecimento criado com sucesso! Bem-vindo ao Space Food.", { severity: "success" });
                navigate("/estabelecimentos");
            }
        }
    };

    return (
        <PageContainer>
            <Typography variant="h5" fontWeight="bold">
                Criar estabelecimento
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Preencha os dados abaixo para começar a usar o Space Food.
            </Typography>

            <FormWrapper>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <RadioCard
                            labelValue="PJ"
                            labelTitle="Pessoa Jurídica"
                            selected={tipoPessoa === "PJ"}
                            onClick={() => handleTipoPessoaChange("PJ")}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <RadioCard
                            labelValue="PF"
                            labelTitle="Pessoa Física"
                            selected={tipoPessoa === "PF"}
                            onClick={() => handleTipoPessoaChange("PF")}
                            fullWidth
                        />
                    </Grid>
                </Grid>

                {formVisivel && (
                    <>
                        <PlanoBannerOuter>
                            <PopularBadge>Popular</PopularBadge>
                            <PlanoBannerWrapper>
                                <PlanoBannerInner>
                                    <WorkspacePremium sx={{ fontSize: 40, color: "#1d70d2", flexShrink: 0 }} />
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="bold">
                                            Plano {PLANO_FREE_TRIAL_NOME} — 7 dias grátis
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Experimente todas as funcionalidades sem nenhum custo durante 7 dias.
                                        </Typography>
                                    </Box>
                                </PlanoBannerInner>
                            </PlanoBannerWrapper>
                        </PlanoBannerOuter>

                        <form onSubmit={handleSubmit(handleSubmitForm)}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <Accordion
                                    defaultExpanded
                                    sx={{
                                        borderRadius: "16px",
                                        "&:before": { display: "none" },
                                        "&.MuiAccordion-root::before": { display: "none" },
                                    }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="h6">Dados do estabelecimento</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            {isPJ ? (
                                                <>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            label="CNPJ*"
                                                            fullWidth
                                                            {...register("cnpj")}
                                                            value={mascaraCNPJ(watch("cnpj") ?? "")}
                                                            error={!!errors.cnpj}
                                                            helperText={<>{errors.cnpj?.message}</>}
                                                            inputProps={{ maxLength: 18 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={6}>
                                                        <TextField
                                                            label="Nome fantasia*"
                                                            fullWidth
                                                            {...register("nomeFantasia")}
                                                            error={!!errors.nomeFantasia}
                                                            helperText={<>{errors.nomeFantasia?.message}</>}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <TextField
                                                            label="Razão social*"
                                                            fullWidth
                                                            {...register("razaoSocial")}
                                                            error={!!errors.razaoSocial}
                                                            helperText={<>{errors.razaoSocial?.message}</>}
                                                        />
                                                    </Grid>
                                                </>
                                            ) : (
                                                <>
                                                    <Grid item xs={12} sm={5}>
                                                        <TextField
                                                            label="CPF*"
                                                            fullWidth
                                                            {...register("cpf")}
                                                            value={mascaraCPF(watch("cpf") ?? "")}
                                                            error={!!errors.cpf}
                                                            helperText={<>{errors.cpf?.message}</>}
                                                            inputProps={{ maxLength: 14 }}
                                                        />
                                                    </Grid>
                                                    <Grid item xs={12} sm={7}>
                                                        <TextField
                                                            label="Nome do estabelecimento*"
                                                            fullWidth
                                                            {...register("nomeFantasia")}
                                                            error={!!errors.nomeFantasia}
                                                            helperText={<>{errors.nomeFantasia?.message}</>}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <Accordion
                                    defaultExpanded
                                    sx={{
                                        borderRadius: "16px",
                                        "&:before": { display: "none" },
                                        "&.MuiAccordion-root::before": { display: "none" },
                                    }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMore />}>
                                        <Typography variant="h6">Endereço</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="CEP"
                                                    fullWidth
                                                    {...register("endereco.cep")}
                                                    value={mascaraCEP(watch("endereco.cep") ?? "")}
                                                    onChange={(e) => {
                                                        setValue("endereco.cep", e.target.value);
                                                        handleBuscarCep(e.target.value);
                                                    }}
                                                    inputProps={{ maxLength: 9 }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                <TextField
                                                    label="Rua*"
                                                    fullWidth
                                                    {...register("endereco.rua")}
                                                    InputLabelProps={{ shrink: !!watch("endereco.rua") || undefined }}
                                                    error={!!enderecoErrors?.rua}
                                                    helperText={<>{enderecoErrors?.rua?.message}</>}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={3}>
                                                <TextField
                                                    label="Número*"
                                                    fullWidth
                                                    {...register("endereco.numero")}
                                                    error={!!enderecoErrors?.numero}
                                                    helperText={<>{enderecoErrors?.numero?.message}</>}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={5}>
                                                <TextField
                                                    label="Complemento"
                                                    fullWidth
                                                    {...register("endereco.complemento")}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="Bairro*"
                                                    fullWidth
                                                    {...register("endereco.bairro")}
                                                    InputLabelProps={{ shrink: !!watch("endereco.bairro") || undefined }}
                                                    error={!!enderecoErrors?.bairro}
                                                    helperText={<>{enderecoErrors?.bairro?.message}</>}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={8}>
                                                <TextField
                                                    label="Cidade*"
                                                    fullWidth
                                                    {...register("endereco.cidade")}
                                                    InputLabelProps={{ shrink: !!watch("endereco.cidade") || undefined }}
                                                    error={!!enderecoErrors?.cidade}
                                                    helperText={<>{enderecoErrors?.cidade?.message}</>}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    label="UF*"
                                                    fullWidth
                                                    {...register("endereco.uf")}
                                                    InputLabelProps={{ shrink: !!watch("endereco.uf") || undefined }}
                                                    error={!!enderecoErrors?.uf}
                                                    helperText={<>{enderecoErrors?.uf?.message}</>}
                                                    inputProps={{ maxLength: 2, style: { textTransform: "uppercase" } }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </AccordionDetails>
                                </Accordion>

                                <ActionsContainer>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={() => navigate("/estabelecimentos")}
                                        disabled={loading}
                                    >
                                        Cancelar
                                    </Button>
                                    {loading ? (
                                        <CircularProgress size={40} />
                                    ) : (
                                        <BotaoFreeTrial variant="contained" size="large" type="submit">
                                            Testar por 7 dias grátis
                                        </BotaoFreeTrial>
                                    )}
                                </ActionsContainer>
                            </Box>
                        </form>
                    </>
                )}
            </FormWrapper>
        </PageContainer>
    );
};
